import { ref } from "vue";
import { defineStore } from "pinia";
import { describeApiError } from "@/api/client";
import type {
  CreatePropertyInput,
  PropertyDetail,
  PropertySummary,
  ReplacePropertyAddressInput,
  ReplacePropertyBedsInput,
  UpdatePropertyInput,
} from "@/api/property-schemas";
import { washboxApi } from "@/api/washbox";
import { keycloakSession } from "@/auth/keycloak-session";

export type PropertiesStatus = "idle" | "loading" | "ready" | "saving" | "error";

function summaryFromDetail(property: PropertyDetail): PropertySummary {
  return {
    id: property.id,
    propertyCode: property.propertyCode,
    propertyName: property.propertyName,
    propertyType: property.propertyType,
    bedroomCount: property.bedroomCount,
    bathroomCount: property.bathroomCount,
    roomCount: property.roomCount,
    sizeSqft: property.sizeSqft,
    status: property.status,
    versionNo: property.versionNo,
    createdAt: property.createdAt,
    updatedAt: property.updatedAt,
    serviceAddress: property.serviceAddress,
  };
}

export const usePropertiesStore = defineStore("properties", () => {
  const status = ref<PropertiesStatus>("idle");
  const properties = ref<PropertySummary[]>([]);
  const currentProperty = ref<PropertyDetail | null>(null);
  const errorMessage = ref<string | null>(null);
  let loading: Promise<void> | null = null;
  let listLoaded = false;
  let epoch = 0;

  keycloakSession.subscribe((authenticated) => {
    if (!authenticated) {
      reset();
    }
  });

  function upsert(property: PropertyDetail): void {
    const summary = summaryFromDetail(property);
    const index = properties.value.findIndex(({ id }) => id === property.id);
    if (property.status === "ARCHIVED") {
      if (index >= 0) {
        properties.value.splice(index, 1);
      }
      return;
    }
    if (index >= 0) {
      properties.value.splice(index, 1, summary);
    } else {
      properties.value.unshift(summary);
    }
  }

  function applyMutation(property: PropertyDetail): PropertyDetail {
    currentProperty.value = property;
    upsert(property);
    status.value = "ready";
    return property;
  }

  function load(force = false): Promise<void> {
    if (!force && listLoaded) {
      return Promise.resolve();
    }
    if (loading !== null) {
      return loading;
    }

    status.value = "loading";
    errorMessage.value = null;
    const loadEpoch = epoch;
    const loadPromise = washboxApi
      .listProperties()
      .then((loadedProperties) => {
        if (epoch !== loadEpoch) {
          return;
        }
        properties.value = loadedProperties;
        listLoaded = true;
        status.value = "ready";
      })
      .catch((error: unknown) => {
        if (epoch !== loadEpoch) {
          return;
        }
        status.value = "error";
        errorMessage.value = describeApiError(error);
      })
      .finally(() => {
        if (loading === loadPromise) {
          loading = null;
        }
      });
    loading = loadPromise;
    return loadPromise;
  }

  function loadProperty(propertyId: string, force = false): Promise<void> {
    if (!force && status.value === "ready" && currentProperty.value?.id === propertyId) {
      return Promise.resolve();
    }
    if (loading !== null) {
      return loading;
    }

    status.value = "loading";
    errorMessage.value = null;
    const loadEpoch = epoch;
    const loadPromise = washboxApi
      .getProperty(propertyId)
      .then((property) => {
        if (epoch !== loadEpoch) {
          return;
        }
        currentProperty.value = property;
        upsert(property);
        status.value = "ready";
      })
      .catch((error: unknown) => {
        if (epoch !== loadEpoch) {
          return;
        }
        currentProperty.value = null;
        status.value = "error";
        errorMessage.value = describeApiError(error);
      })
      .finally(() => {
        if (loading === loadPromise) {
          loading = null;
        }
      });
    loading = loadPromise;
    return loadPromise;
  }

  async function mutate(operation: () => Promise<PropertyDetail>): Promise<PropertyDetail> {
    const mutationEpoch = epoch;
    status.value = "saving";
    errorMessage.value = null;
    try {
      const property = await operation();
      if (epoch !== mutationEpoch) {
        return property;
      }
      return applyMutation(property);
    } catch (error) {
      if (epoch === mutationEpoch) {
        status.value = listLoaded || currentProperty.value !== null ? "ready" : "error";
        errorMessage.value = describeApiError(error);
      }
      throw error;
    }
  }

  function create(input: CreatePropertyInput, idempotencyKey: string): Promise<PropertyDetail> {
    return mutate(() => washboxApi.createProperty(input, idempotencyKey));
  }

  function update(propertyId: string, input: UpdatePropertyInput): Promise<PropertyDetail> {
    return mutate(() => washboxApi.updateProperty(propertyId, input));
  }

  function replaceAddress(
    propertyId: string,
    input: ReplacePropertyAddressInput,
  ): Promise<PropertyDetail> {
    return mutate(() => washboxApi.replacePropertyAddress(propertyId, input));
  }

  function replaceBeds(
    propertyId: string,
    input: ReplacePropertyBedsInput,
  ): Promise<PropertyDetail> {
    return mutate(() => washboxApi.replacePropertyBeds(propertyId, input));
  }

  function archive(propertyId: string, versionNo: number): Promise<PropertyDetail> {
    return mutate(() => washboxApi.archiveProperty(propertyId, versionNo));
  }

  function reset(): void {
    epoch += 1;
    status.value = "idle";
    properties.value = [];
    currentProperty.value = null;
    errorMessage.value = null;
    loading = null;
    listLoaded = false;
  }

  return {
    status,
    properties,
    currentProperty,
    errorMessage,
    load,
    loadProperty,
    create,
    update,
    replaceAddress,
    replaceBeds,
    archive,
    reset,
  };
});
